import classNames from "classnames";
import { TemplateKey } from "PFApp/constants/templates";
import { Button } from "PFComponents/button";
import { Icon } from "PFComponents/icon";
import { Typography } from "PFComponents/typography";
import { Id } from "PFTypes";
import { CSSProperties, ReactElement } from "react";
import { useTranslation } from "react-i18next";

import css from "./share_bar.module.scss";
import { ShareButton } from "./share_button";

type ShareWrapperProps = {
  activitiesToShare: Id[];
  onShare: (activityId: Id) => void;
  activityId: Id;
  disabled?: boolean;
  children: ReactElement;
};

export const ShareWrapper = ({
  activitiesToShare,
  onShare,
  activityId,
  disabled,
  children
}: ShareWrapperProps) => {
  const shareMode = !!activitiesToShare?.length;
  const addedToShare = activitiesToShare.includes(activityId);

  /* eslint-disable jsx-a11y/click-events-have-key-events */
  /* eslint-disable jsx-a11y/no-static-element-interactions */
  return (
    <div
      className={classNames(css.shareWrapper, {
        [css.shareMode]: shareMode,
        [css.sharing]: addedToShare,
        [css.disabled]: disabled
      })}
      onClick={shareMode ? () => onShare(activityId) : undefined}
    >
      {addedToShare && (
        <div className={css.addedToShareIcon}>
          <Icon name="check" size="sm" color="paletteWhite0" />
        </div>
      )}
      <div className={classNames(css.content, { [css.shareMode]: shareMode, [css.sharing]: addedToShare })}>
        {children}
      </div>
    </div>
  );
};

type ShareBarProps = {
  activityIds: number[];
  onCancel?: () => void;
  templateKey?: TemplateKey;
  style?: CSSProperties;
};

export const ShareBar = ({ activityIds, onCancel, templateKey, style }: ShareBarProps) => {
  const { t } = useTranslation(["core", "translation"]);
  const show = activityIds.length;

  return (
    <div className={classNames(css.root, { [css.hide]: !show })} style={style}>
      <h3 className={css.title}>
        {t("core:activityShare.barHeader", { activityType: templateKey || "activity" })}
      </h3>
      <span className={css.roleSelected}>
        <span>
          {t("core:activityShare.barSelectActivities", { activityType: templateKey || "activities" })}:&nbsp;
        </span>
        <Typography withMargin variant="bodyBold" tag="span" className={css.rolesCountText}>
          {templateKey
            ? t("core:activityShare.shareActivityTemplates", {
                count: activityIds.length,
                templateKey
              })
            : t("core:activityShare.shareActivities", { count: activityIds.length })}
        </Typography>
        <div className={css.shareActions}>
          <Button kind="secondary" onClick={onCancel}>
            {t("translation:cancel")}
          </Button>
          <ShareButton
            templateKey={templateKey}
            activityIds={activityIds}
            onSuccess={() => {
              onCancel && onCancel();
            }}
          />
        </div>
      </span>
    </div>
  );
};
